<template>
    <ejs-querybuilder ref="querybuilder" 
                      :ruleChange="filterChanged" 
                      :columns="currentFilterFields" 
                      :locale="$config.currentLocale" 
                      :maxGroupCount="maxGroupCount">
</ejs-querybuilder>
</template>

<script>
    import $ from 'jquery';
    import Vue from 'vue';
    import BootstrapVue from 'bootstrap-vue';
    import { QueryBuilderPlugin } from "@syncfusion/ej2-vue-querybuilder";
    import { MultiSelect, CheckBoxSelection, DropDownList } from '@syncfusion/ej2-dropdowns';
    import { createElement, getComponent } from "@syncfusion/ej2-base";
    import { Query } from '@syncfusion/ej2-data';

    import FilterTimespanFieldTemplate from "./FilterTimespanFieldTemplate";

    Vue.use(BootstrapVue);
    Vue.use(QueryBuilderPlugin)
    
    Vue.component('FilterTimespanFieldTemplate', FilterTimespanFieldTemplate);

    export default {
        name: 'FilterBuilder',
        provide: {
            multiselect: [CheckBoxSelection]
        },
        model: {
            event: 'change',
            prop: 'filter'
        },
        props: {
            fields: {
                type: Array,
                required: true
            },
            filter: {
                type: Object,
                default: null
            },
            maxGroupCount: {
                type: Number,
                default: 5
            }
        },

        data: function () {
            return {
                currentFields: this.fields,
                currentFilterFields: null,
                currentFilter: this.filter,
                filterDisplayText: null,

                conditionToDisplayTextMapping: new Map([
                    ["and", "And"],
                    ["or", "Or"]
                ]),
                operatorToDisplayTextMapping: new Map([
                    ["startswith", "Starts With"],
                    ["endswith", "Ends With"],
                    ["contains", "Contains"],
                    ["equal", "="],
                    ["notequal", "!="],
                    ["in", "In"],
                    ["notin", "Not In"],
                    ["isempty", "Is Empty"],
                    ["isnotempty", "Is Not Empty"],
                    ["greaterthanorequal", ">="],
                    ["greaterthan", ">"],
                    ["lessthan", "<"],
                    ["lessthanorequal", "<="],
                    ["between", "Between"],
                    ["notbetween", "Not Between"]
                ]),
                fieldToDisplayTextMapping: null,
                timespanFormat:""
            }
        },
        watch: {
            fields: function () {
                var fieldsString = this.fields ? JSON.stringify(this.fields) : null;
                var previousFieldsString = this.currentFields ? JSON.stringify(this.currentFields) : null;
                this.currentFields = this.fields;
                this.createCurrentFilterFields();
                if (fieldsString != previousFieldsString) {
                    this.currentFilter = {
                        condition: "and",
                        rules: [{
                            field: null,
                            operator: null,
                            value: null
                        }]
                    }
                    this.$nextTick(() => {
                        if (this.$refs.querybuilder) {
                            this.$refs.querybuilder.ej2Instances.reset();
                            this.$refs.querybuilder.ej2Instances.setRules(this.currentFilter);
                        }
                    });
                }
            },
            filter: function () {
                this.currentFilter = this.filter;
                if (this.currentFilter == null) {
                    this.currentFilter = {
                        condition: "and",
                        rules: [{
                            field: null,
                            operator: null,
                            value: null
                        }]
                    }
                }
                this.filterDisplayText = this.createFilterDisplayText(this.currentFilter);
                this.$nextTick(() => {
                    if (this.$refs.querybuilder) this.$refs.querybuilder.ej2Instances.setRules(this.currentFilter);
                });
            }
        },
        methods: {
            createTimespanTemplate: function () {
                return {
                    template: FilterTimespanFieldTemplate,
                    data: {
                        queryBuilder: this.$refs.querybuilder,
                        format:this.timespanFormat
                    }
                }
            },
            createCurrentFilterFields: function () {
                this.currentFilterFields = [];
                this.fieldToDisplayTextMapping = new Map();
                if (this.fields) {
                    this.fields.forEach(field => {
                        var newField = { field: field.field };
                        if (field.label) newField.label = field.label;
                        else field.label = field.field;
                        this.fieldToDisplayTextMapping.set(field.field, field.label);
                        if (field.type == "multiselect") {
                            if (!field.options || !field.options.length) return; //if there are no options that this cannot work
                            newField.type = "string";
                            //newField.values = field.options;
                            if (field.operators) newField.operators = field.operators;
                            else {
                                newField.operators = [
                                    { key: 'In', value: 'in' },
                                    { key: 'Not In', value: 'notin' }
                                ];
                            }
                            newField.template = {
                                create: () => {
                                    return createElement('input', { attrs: { 'type': 'text' } });
                                },
                                destroy: (args) => {
                                    let multiselect = getComponent(document.getElementById(args.elementId), 'multiselect');
                                    if (multiselect)
                                        multiselect.destroy();
                                },
                                write: (args) => {
                                    //args.field args.operator args.values args.dataSource
                                    let multiSelectObj = new MultiSelect({
                                        dataSource: field.options,
                                        value: Array.isArray(args.values) ? args.values : [],
                                        fields: { text: 'text', value: 'value' },
                                        placeholder: field.placeholder,
                                        mode: 'CheckBox',
                                        allowFiltering: true,
                                        showSelectAll: true,
                                        filtering: (e) => {
                                            var query = new Query();
                                            //frame the query based on search string with filter type.
                                            query = (e.text != "") ? query.where("text", "contains", e.text, true) : query;
                                            //pass the filter data source, filter query to updateData method.
                                            e.updateData(field.options, query);
                                        },
                                        change: (e) => {
                                            this.$refs.querybuilder.$el.ej2_instances[0].notifyChange(e.value, e.element);
                                        }
                                    });
                                    multiSelectObj.appendTo('#' + args.elements.id);
                                }
                            }
                        } else if (field.type == "boolean") {
                            newField.type = field.type;
                            if (field.operators) newField.operators = field.operators;
                            else {
                                newField.operators = [
                                    { key: 'Equal', value: 'equal' },
                                    { key: 'Not Equal', value: 'notequal' }
                                ];
                            }
                            if (field.options && (field.options.length == 2)) {
                                newField.template = {
                                    create: () => {
                                        return createElement('input', { attrs: { 'type': 'text' } });
                                    },
                                    destroy: (args) => {
                                        let dropdownlist = getComponent(document.getElementById(args.elementId), 'dropdownlist');
                                        if (dropdownlist)
                                            dropdownlist.destroy();
                                    },
                                    write: (args) => {
                                        let dropDownObj = new DropDownList({
                                            dataSource: field.options,
                                            value: (args.values === undefined) ? false : args.values,
                                            fields: { text: 'text', value: 'value' },
                                            change: (e) => {
                                                this.$refs.querybuilder.$el.ej2_instances[0].notifyChange(e.itemData.value, e.element);
                                            }
                                        });
                                        dropDownObj.appendTo('#' + args.elements.id);
                                    }
                                }
                            } else if (field.values) {
                                newField.values = field.values;
                            }
                        } else if (field.type == "number") {
                            newField.type = field.type;
                            if (field.format) newField.format = field.format;
                            if (field.operators) newField.operators = field.operators;
                            else {
                                newField.operators = [
                                    { key: 'Equal', value: 'equal' },
                                    { key: 'Greater Than', value: 'greaterthan' },
                                    { key: 'Greater Than Or Equal', value: 'greaterthanorequal' },
                                    { key: 'Less Than', value: 'lessthan' },
                                    { key: 'Less Than Or Equal', value: 'lessthanorequal' },
                                    { key: 'Not Equal', value: 'notequal' }
                                ];
                            }
                        } else if (field.type == "timespan") {
                            newField.type = "timespan";
                            newField.operators = [
                                { key: 'Older Than', value: 'lessthan' },
                                { key: 'Younger Than', value: 'greaterthan' }
                            ];
                            if (field.format) {
                                newField.format = field.format;
                                this.timespanFormat = field.format;
                            }
                            newField.template = this.createTimespanTemplate;
                        } else {
                            newField.type = field.type;
                            if (field.format) newField.format = field.format;
                            if (field.operators) newField.operators = field.operators;
                        }
                        this.currentFilterFields.push(newField);
                    });

                    this.currentFilterFields.sort((a, b) => {
                        var nameA = a.label.toUpperCase();
                        var nameB = b.label.toUpperCase();
                        if (nameA < nameB) return -1;
                        if (nameA > nameB) return 1;
                        return 0;
                    });
                }
            },
            filterChanged: function (args) {
                this.currentFilter = args.rule;
                this.filterDisplayText = this.createFilterDisplayText(this.currentFilter);
                this.$emit('change', this.currentFilter);
            },
            createFilterDisplayText: function (filter) {
                var displayText = null;
                if (filter && filter.rules) {
                    filter.rules.forEach(rule => {
                        if (rule.rules) {
                            var subFilterDisplayText = this.createFilterDisplayText(rule);
                            if (subFilterDisplayText) {
                                if (displayText) displayText += ' ' + this.toDisplayText(filter.condition, this.conditionToDisplayTextMapping) + ' ';
                                else displayText = '';
                                displayText += subFilterDisplayText;
                            }
                        } else if (rule.field && rule.operator) {
                            if (displayText) displayText += ' ' + this.toDisplayText(filter.condition, this.conditionToDisplayTextMapping) + ' ';
                            else displayText = '';
                            displayText += this.toDisplayText(rule.field, this.fieldToDisplayTextMapping);
                            displayText += ' ';
                            displayText += this.toDisplayText(rule.operator, this.operatorToDisplayTextMapping);
                            if (rule.value != null) {
                                displayText += ' ';
                                displayText += this.valueToDisplayText(rule.field, rule.value);
                            }
                        }
                    });
                }
                return displayText;
            },
            toDisplayText: function (key, map) {
                if (!map.has(key)) return null;
                return map.get(key);
            },
            valueToDisplayText: function (field, value) {
                if (value == null) return null;
                var fieldObj = this.fields.find(f => f.field == field);
                if (!fieldObj) return null;
                if ((fieldObj.type == "multiselect") && (fieldObj.options) && Array.isArray(value)) {
                    var displayText = '[';
                    value.forEach(v => {
                        var option = fieldObj.options.find(o => o.value == v);
                        if (option) {
                            if (displayText.length > 1) displayText += ",'";
                            else displayText += "'";
                            displayText += option.text;
                            displayText += "'"
                        }
                    });
                    displayText += ']';
                    return displayText;
                }
                if ((fieldObj.type == "boolean") && (fieldObj.options)) {
                    var option = fieldObj.options.find(o => o.value == value);
                    if (option) {
                        return option.text;
                    }
                }
                return value;
            },
            refresh: function () {
                if (this.$refs.querybuilder) this.$refs.querybuilder.ej2Instances.setRules(this.currentFilter);
            }
        },
        mounted: function () {
            this.createCurrentFilterFields();
            if (this.currentFilter == null) {
                this.currentFilter = {
                    condition: "and",
                    rules: [{
                        field: null,
                        operator: null,
                        value: null
                    }]
                }
            }
            this.filterDisplayText = this.createFilterDisplayText(this.currentFilter);
            this.$nextTick(() => {
                this.refresh();
            });
        },
        updated: function () {
            //this is a custom fix for the issue that especially in a modal, the multi-select does not properly render its initial value after the query builder opens
            //essentially the element that display the number of selected entries has zero width - this gets rectified with the following code that simply removes that
            this.$nextTick(function () {
                $('.e-multi-select-wrapper .e-delim-view.e-delim-values.e-delim-total').css('width', 'initial');
            })
        }
    }
</script>
<style scoped>
    .e-rule-field {
        display: flex;
        align-items: center;
    }

    .e-template-value {
        flex-grow: 1;
    }

    .e-template-value .e-multiselect {
        max-width: none !important;
    }

    .e-template-value .e-multi-select-wrapper {
        max-width: none !important;
    }
    
</style>